/**
 * Pages close to a mistyped address (B3-404): the 404 offers them under
 * « Retour à mon espace ». Candidates are the pages of the reader's space
 * (the dialer menu, the back office, or the public pages without a
 * session); the closest by edit distance on the path or its last segment.
 */
import { DIALER_VIEW_IDS, VIEW_NAV_LABEL, pathForView } from '@/layouts/dialerRoutes';
import type { Space } from './homeForRole';

export interface RouteSuggestion { path: string; labelKey: string }

const DIALER_ADMIN_ONLY = new Set(['client-portal', 'team', 'billing']);

const ADMIN_ROUTES: RouteSuggestion[] = [
  { path: '/AdminOrganisations', labelKey: 'admin:superAdminLayout.nav.organisations' },
  { path: '/AdminAuditLogs', labelKey: 'admin:superAdminLayout.nav.auditLogs' },
  { path: '/AdminClientErrors', labelKey: 'admin:superAdminLayout.nav.clientErrors' },
  { path: '/AdminPlatformHealth', labelKey: 'admin:superAdminLayout.nav.platformHealth' },
  { path: '/AdminNotifications', labelKey: 'admin:superAdminLayout.nav.notifications' },
  { path: '/TwilioConfig', labelKey: 'admin:superAdminLayout.nav.twilio' },
  { path: '/AdminAmdEngine', labelKey: 'admin:superAdminLayout.nav.amdEngine' },
  { path: '/AdminCallingProvider', labelKey: 'admin:superAdminLayout.nav.callingProvider' },
];

const PUBLIC_ROUTES: RouteSuggestion[] = [
  { path: '/Login', labelKey: 'admin:loginPage.title' },
  { path: '/confidential', labelKey: 'pages:legal.privacyPolicy.title' },
  { path: '/cgu', labelKey: 'pages:legal.termsOfService.shortTitle' },
  { path: '/forgot-password', labelKey: 'admin:passwordReset.requestTitle' },
];

function dialerRoutes(role: string | null | undefined): RouteSuggestion[] {
  return DIALER_VIEW_IDS.flatMap((id) => {
    const labelKey = VIEW_NAV_LABEL[id];
    if (!labelKey || (role !== 'tenant_admin' && DIALER_ADMIN_ONLY.has(id))) return [];
    return [{ path: pathForView(id), labelKey }];
  });
}

export function candidateRoutes(space: Space | null, role?: string | null): RouteSuggestion[] {
  if (space === 'dialer') return dialerRoutes(role);
  if (space === 'admin') return ADMIN_ROUTES;
  if (space === null) return PUBLIC_ROUTES;
  return []; // portal: one page, already the main button
}

function levenshtein(a: string, b: string): number {
  const row: number[] = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    let prev = i - 1;
    row[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const above = row[j] ?? 0;
      row[j] = Math.min(above + 1, (row[j - 1] ?? 0) + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1));
      prev = above;
    }
  }
  return row[b.length] ?? 0;
}

const normalize = (p: string) => (p.split(/[?#]/)[0] ?? '').replace(/\/+$/, '').toLowerCase() || '/';
const lastSegment = (p: string) => p.split('/').filter(Boolean).pop() ?? '';

export function suggestRoutes(requested: string, candidates: RouteSuggestion[], exclude: string, limit = 3): RouteSuggestion[] {
  const req = normalize(requested);
  const reqLast = lastSegment(req);
  const scored = candidates
    .filter((c) => normalize(c.path) !== normalize(exclude))
    .map((c) => {
      const path = normalize(c.path);
      const last = lastSegment(path);
      let score = Math.min(levenshtein(req, path), levenshtein(reqLast, last));
      if (reqLast.length >= 4 && last.length >= 4 && (last.includes(reqLast) || reqLast.includes(last))) score = Math.min(score, 1);
      const tolerance = Math.max(2, Math.floor(Math.min(reqLast.length, last.length) / 3));
      return { c, score, ok: score <= tolerance };
    })
    .filter((s) => s.ok)
    .sort((a, b) => a.score - b.score);
  const seen = new Set<string>();
  const out: RouteSuggestion[] = [];
  for (const { c } of scored) {
    if (seen.has(c.path)) continue;
    seen.add(c.path);
    out.push(c);
    if (out.length === limit) break;
  }
  return out;
}
